import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Cookie, X } from "lucide-react";
import { Button } from "@/components/ui/button";

const STORAGE_KEY = "fortuna_cookie_consent_v1";

const defaultPrefs = {
  necessary: true,
  statistics: false,
  marketing: false,
};

export const CookieBanner = () => {
  const [open, setOpen] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [prefs, setPrefs] = useState(defaultPrefs);

  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (!saved) {
      setOpen(true);
    } else {
      try {
        setPrefs({ ...defaultPrefs, ...JSON.parse(saved) });
      } catch {
        setOpen(true);
      }
    }

    const handler = () => {
      const cur = localStorage.getItem(STORAGE_KEY);
      if (cur) {
        try {
          setPrefs({ ...defaultPrefs, ...JSON.parse(cur) });
        } catch {
          /* noop */
        }
      }
      setShowSettings(true);
      setOpen(true);
    };
    window.addEventListener("open-cookie-settings", handler);
    return () => window.removeEventListener("open-cookie-settings", handler);
  }, []);

  const save = (next) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    setPrefs(next);
    setOpen(false);
    setShowSettings(false);
  };

  const acceptAll = () =>
    save({ necessary: true, statistics: true, marketing: true });
  const onlyNecessary = () =>
    save({ necessary: true, statistics: false, marketing: false });
  const saveSettings = () => save(prefs);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ y: 80, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 80, opacity: 0 }}
          transition={{ duration: 0.4, ease: "easeOut" }}
          data-testid="cookie-banner"
          className="fixed inset-x-0 bottom-0 z-[60] p-3 md:p-5"
        >
          <div className="max-w-5xl mx-auto bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden">
            <div className="p-5 md:p-6">
              <div className="flex items-start gap-4">
                <div className="hidden sm:flex w-11 h-11 rounded-md bg-[#EA580C]/10 text-[#EA580C] items-center justify-center shrink-0">
                  <Cookie className="w-5 h-5" />
                </div>
                <div className="flex-1">
                  <h3 className="font-heading font-bold text-[#0B192C] text-base md:text-lg">
                    Sütiket használunk
                  </h3>
                  <p className="mt-1.5 text-sm text-gray-600 leading-relaxed">
                    A weboldal sütiket használ a megfelelő működés, a
                    felhasználói élmény javítása, statisztikai elemzés és
                    marketing célok érdekében. A szükséges sütik mindig
                    aktívak, a statisztikai és marketing sütiket csak az Ön
                    hozzájárulása esetén használjuk.{" "}
                    <Link
                      to="/cookie-tajekoztato"
                      className="text-[#EA580C] hover:underline font-medium"
                    >
                      Részletek
                    </Link>
                  </p>
                </div>
                <button
                  data-testid="cookie-banner-close"
                  onClick={() => setOpen(false)}
                  aria-label="Bezárás"
                  className="text-gray-400 hover:text-gray-600 transition-colors p-1"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              {showSettings && (
                <div className="mt-5 grid sm:grid-cols-3 gap-3 border-t border-gray-100 pt-5">
                  <label className="flex items-start gap-2 p-3 rounded-md border border-gray-200 bg-gray-50 cursor-not-allowed opacity-70">
                    <input
                      type="checkbox"
                      checked
                      disabled
                      className="mt-0.5 accent-[#EA580C]"
                      data-testid="cookie-pref-necessary"
                    />
                    <div>
                      <div className="font-heading font-semibold text-sm text-[#0B192C]">
                        Szükséges
                      </div>
                      <div className="text-xs text-gray-500 mt-0.5">
                        Mindig aktív
                      </div>
                    </div>
                  </label>
                  <label className="flex items-start gap-2 p-3 rounded-md border border-gray-200 hover:border-[#EA580C] cursor-pointer transition-colors">
                    <input
                      type="checkbox"
                      checked={prefs.statistics}
                      onChange={(e) =>
                        setPrefs((p) => ({ ...p, statistics: e.target.checked }))
                      }
                      className="mt-0.5 accent-[#EA580C]"
                      data-testid="cookie-pref-statistics"
                    />
                    <div>
                      <div className="font-heading font-semibold text-sm text-[#0B192C]">
                        Statisztikai
                      </div>
                      <div className="text-xs text-gray-500 mt-0.5">
                        Forgalom-elemzés
                      </div>
                    </div>
                  </label>
                  <label className="flex items-start gap-2 p-3 rounded-md border border-gray-200 hover:border-[#EA580C] cursor-pointer transition-colors">
                    <input
                      type="checkbox"
                      checked={prefs.marketing}
                      onChange={(e) =>
                        setPrefs((p) => ({ ...p, marketing: e.target.checked }))
                      }
                      className="mt-0.5 accent-[#EA580C]"
                      data-testid="cookie-pref-marketing"
                    />
                    <div>
                      <div className="font-heading font-semibold text-sm text-[#0B192C]">
                        Marketing
                      </div>
                      <div className="text-xs text-gray-500 mt-0.5">
                        Hirdetések, remarketing
                      </div>
                    </div>
                  </label>
                </div>
              )}

              <div className="mt-5 flex flex-col sm:flex-row gap-2 sm:items-center sm:justify-end">
                {showSettings ? (
                  <Button
                    data-testid="cookie-save-settings"
                    onClick={saveSettings}
                    className="bg-[#EA580C] hover:bg-[#C2410C] text-white h-10 px-5"
                  >
                    Beállítások mentése
                  </Button>
                ) : (
                  <Button
                    data-testid="cookie-open-settings"
                    onClick={() => setShowSettings(true)}
                    variant="outline"
                    className="h-10 px-5 border-gray-300"
                  >
                    Beállítások
                  </Button>
                )}
                <Button
                  data-testid="cookie-only-necessary"
                  onClick={onlyNecessary}
                  variant="outline"
                  className="h-10 px-5 border-gray-300"
                >
                  Csak szükséges sütik
                </Button>
                <Button
                  data-testid="cookie-accept-all"
                  onClick={acceptAll}
                  className="bg-[#0B192C] hover:bg-[#1E3E62] text-white h-10 px-5"
                >
                  Összes elfogadása
                </Button>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CookieBanner;
